import React, { useEffect, useRef, useState } from "react";
import { auth, firestore, storage } from "../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import "./profile.css";

const ProfilePage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formValues, setFormValues] = useState({ name: "", paymentMethod: "" });
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const docRef = doc(firestore, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          setFormValues({
            name: data.name || "",
            paymentMethod: data.paymentMethod || "",
          });
        } else {
          console.log("No user document found.");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const user = auth.currentUser;
    if (!user) return;

    const storageRef = ref(storage, `profilePictures/${user.uid}`);
    setUploading(true);

    try {
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await updateDoc(doc(firestore, "users", user.uid), {
        profilePicture: downloadURL,
      });

      setUserData((prev) => ({ ...prev, profilePicture: downloadURL }));
    } catch (error) {
      console.error("Image upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleEditToggle = () => {
    setEditing(!editing);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        name: formValues.name,
        paymentMethod: formValues.paymentMethod,
      });

      setUserData((prev) => ({
        ...prev,
        name: formValues.name,
        paymentMethod: formValues.paymentMethod,
      }));

      setEditing(false);
      console.log("User profile updated.");
    } catch (error) {
      console.error("Error saving profile:", error);
    }
  };

  if (loading) return <div className="spinner">Loading...</div>;
  if (!userData) return <div className="error">No user data found.</div>;

  return (
    <div className="profile-page">
      <div className="profile-card">
        <img
          src={userData.profilePicture || "/default-profile.jpg"}
          alt="Profile"
          className="profile-image"
          onClick={triggerFileInput}
          style={{ cursor: "pointer" }}
        />
        {uploading && <p className="uploading">Uploading image...</p>}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />

        {editing ? (
          <>
            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleChange}
              placeholder="Name"
            />
            <input
              type="text"
              name="paymentMethod"
              value={formValues.paymentMethod}
              onChange={handleChange}
              placeholder="Payment Method"
            />
            <button onClick={handleSaveChanges}>Save</button>
          </>
        ) : (
          <>
            <h2>{userData.name || "Anonymous"}</h2>
            <p><strong>Email:</strong> {userData.email}</p>
            <p><strong>Payment Method:</strong> {userData.paymentMethod || "Not set"}</p>
            <button onClick={handleEditToggle}>Edit Profile</button>
          </>
        )}

        <p><strong>Earnings:</strong> ${userData.earnings?.toFixed(2) || "0.00"}</p>
        <p><strong>Referrals:</strong> {userData.referrals || 0}</p>
        <p><strong>Rank:</strong> {userData.rank || "Member"}</p>

        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </div>
    </div>
  );
};

export default ProfilePage;
